/**
 * Settings panel — text speed, auto-advance delay, volume sliders, mute, skip-read-only, language.
 * Persists to localStorage under `kan-vn:settings`.
 */

import { AudioEngine } from '../engine/audio';
import type { Locale } from '../engine/game';

const SETTINGS_KEY = 'kan-vn:settings';

export interface VnSettings {
  /** Characters per second for typewriter (10–100). */
  textSpeed: number;
  /** Auto-advance delay in ms (300–3000). */
  autoAdvanceDelay: number;
  /** Master volume 0–1. */
  masterVolume: number;
  /** SFX volume 0–1. */
  sfxVolume: number;
  /** Ambient BGM volume 0–1. */
  bgmVolume?: number;
  /** Whether audio is globally muted. */
  muted: boolean;
  /** Only skip text that has already been read. */
  skipReadOnly: boolean;
  /** Whether parallax background motion is enabled (KAN-25). */
  parallaxEnabled?: boolean;
  /** Active display locale ('en' | 'zh'). */
  locale: Locale;
}

const DEFAULT_SETTINGS: VnSettings = {
  textSpeed: 35,
  autoAdvanceDelay: 1500,
  // Low ambient defaults — keeps BGM and SFX gentle so dialogue stays primary.
  masterVolume: 0.5,
  sfxVolume: 0.55,
  bgmVolume: 0.5,
  muted: false,
  skipReadOnly: true,
  parallaxEnabled: true,
  locale: 'en',
};

/** Load persisted settings, falling back to defaults. */
export function loadSettings(): VnSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed: unknown = JSON.parse(raw);
    if (isValidSettings(parsed)) {
      return parsed;
    }
    return { ...DEFAULT_SETTINGS };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

/** Persist settings to localStorage. */
export function saveSettings(settings: VnSettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

function isValidSettings(value: unknown): value is VnSettings {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj['textSpeed'] === 'number' &&
    typeof obj['autoAdvanceDelay'] === 'number' &&
    typeof obj['masterVolume'] === 'number' &&
    typeof obj['sfxVolume'] === 'number' &&
    typeof obj['muted'] === 'boolean' &&
    typeof obj['skipReadOnly'] === 'boolean'
  );
}

export interface SettingsPanelCallbacks {
  onClose: () => void;
  onSettingsChanged?: (settings: VnSettings) => void;
}

/**
 * Render the settings modal overlay.
 */
export function renderSettingsPanel(
  root: HTMLElement,
  callbacks: SettingsPanelCallbacks,
): void {
  // Remove any existing settings modal
  const existing = root.querySelector('#settings-modal');
  if (existing) existing.remove();

  const settings = loadSettings();
  const audio = AudioEngine.getInstance();

  const overlay = document.createElement('div');
  overlay.id = 'settings-modal';

  const panel = document.createElement('div');
  panel.className = 'sl-panel settings-panel';

  // Header
  const header = document.createElement('div');
  header.className = 'sl-header';

  const title = document.createElement('h2');
  title.textContent = 'Settings';
  header.appendChild(title);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'sl-close';
  closeBtn.textContent = '✕';
  closeBtn.addEventListener('click', () => close());
  header.appendChild(closeBtn);
  panel.appendChild(header);

  // Body
  const body = document.createElement('div');
  body.className = 'settings-body';

  // ── Text Speed ──
  body.appendChild(
    createSliderRow('Text Speed', settings.textSpeed, 10, 100, 1, 'cps', (v) => {
      settings.textSpeed = v;
      persist();
    }),
  );

  // ── Auto-advance Delay ──
  body.appendChild(
    createSliderRow('Auto Delay', settings.autoAdvanceDelay, 300, 3000, 100, 'ms', (v) => {
      settings.autoAdvanceDelay = v;
      persist();
    }),
  );

  // ── Master Volume ──
  body.appendChild(
    createVolumeRow('Master', settings.masterVolume, settings.muted, {
      onVolume: (v) => {
        settings.masterVolume = v;
        // Apply master to both audio buses proportionally
        audio.sfxVolume = settings.sfxVolume * v;
        audio.bgmVolume = (settings.bgmVolume ?? 0.5) * v;
        persist();
      },
      onMute: (m) => {
        settings.muted = m;
        audio.sfxMuted = m;
        audio.bgmMuted = m;
        persist();
      },
    }),
  );

  // ── BGM Volume ──
  body.appendChild(
    createVolumeRow('Music', settings.bgmVolume ?? 0.5, audio.bgmMuted, {
      onVolume: (v) => {
        settings.bgmVolume = v;
        audio.bgmVolume = v * settings.masterVolume;
        persist();
      },
      onMute: (m) => {
        audio.bgmMuted = m;
        persist();
      },
    }),
  );

  // ── SFX Volume ──
  body.appendChild(
    createVolumeRow('SFX', settings.sfxVolume, audio.sfxMuted, {
      onVolume: (v) => {
        settings.sfxVolume = v;
        audio.sfxVolume = v * settings.masterVolume;
        persist();
      },
      onMute: (m) => {
        audio.sfxMuted = m;
        persist();
      },
    }),
  );

  // ── Skip Read-Only Toggle ──
  body.appendChild(
    createToggleRow('Skip read text only', settings.skipReadOnly, (v) => {
      settings.skipReadOnly = v;
      persist();
    }),
  );

  // ── Language / 语言 ──
  body.appendChild(
    createLocaleRow(settings.locale, (locale) => {
      settings.locale = locale;
      persist();
      document.dispatchEvent(
        new CustomEvent<{ locale: Locale }>('vn-locale-change', { detail: { locale } }),
      );
    }),
  );

  panel.appendChild(body);
  overlay.appendChild(panel);

  // Close on overlay background click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  // Esc to close
  const escHandler = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };
  document.addEventListener('keydown', escHandler);

  root.appendChild(overlay);

  function persist(): void {
    saveSettings(settings);
    callbacks.onSettingsChanged?.(settings);
  }

  function close(): void {
    document.removeEventListener('keydown', escHandler);
    overlay.remove();
    callbacks.onClose();
  }
}

// ── Helpers ──

interface VolumeRowCallbacks {
  onVolume: (value: number) => void;
  onMute: (muted: boolean) => void;
}

function createVolumeRow(
  label: string,
  initialVolume: number,
  initialMuted: boolean,
  callbacks: VolumeRowCallbacks,
): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'settings-row';

  const labelEl = document.createElement('span');
  labelEl.className = 'settings-label';
  labelEl.textContent = label;
  row.appendChild(labelEl);

  const muteBtn = document.createElement('button');
  muteBtn.className = 'audio-mute-btn';
  muteBtn.setAttribute('aria-label', `${initialMuted ? 'Unmute' : 'Mute'} ${label}`);
  muteBtn.textContent = initialMuted ? '🔇' : '🔊';
  muteBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const nowMuted = muteBtn.textContent === '🔊';
    muteBtn.textContent = nowMuted ? '🔇' : '🔊';
    muteBtn.setAttribute('aria-label', `${nowMuted ? 'Unmute' : 'Mute'} ${label}`);
    slider.disabled = nowMuted;
    callbacks.onMute(nowMuted);
  });
  row.appendChild(muteBtn);

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.className = 'audio-slider';
  slider.min = '0';
  slider.max = '100';
  slider.value = String(Math.round(initialVolume * 100));
  slider.disabled = initialMuted;
  slider.setAttribute('aria-label', `${label} volume`);
  slider.addEventListener('input', () => {
    const v = parseInt(slider.value, 10) / 100;
    pct.textContent = `${slider.value}%`;
    callbacks.onVolume(v);
  });
  row.appendChild(slider);

  const pct = document.createElement('span');
  pct.className = 'audio-pct';
  pct.textContent = `${Math.round(initialVolume * 100)}%`;
  row.appendChild(pct);

  return row;
}

function createSliderRow(
  label: string,
  initialValue: number,
  min: number,
  max: number,
  step: number,
  unit: string,
  onChange: (value: number) => void,
): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'settings-row';

  const labelEl = document.createElement('span');
  labelEl.className = 'settings-label';
  labelEl.textContent = label;
  row.appendChild(labelEl);

  // Spacer to align with volume rows (no mute button)
  const spacer = document.createElement('span');
  spacer.className = 'settings-spacer';
  row.appendChild(spacer);

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.className = 'audio-slider';
  slider.min = String(min);
  slider.max = String(max);
  slider.step = String(step);
  slider.value = String(initialValue);
  slider.setAttribute('aria-label', label);
  slider.addEventListener('input', () => {
    const v = parseInt(slider.value, 10);
    valueEl.textContent = `${v}${unit}`;
    onChange(v);
  });
  row.appendChild(slider);

  const valueEl = document.createElement('span');
  valueEl.className = 'audio-pct';
  valueEl.textContent = `${initialValue}${unit}`;
  row.appendChild(valueEl);

  return row;
}

function createToggleRow(
  label: string,
  initialValue: boolean,
  onChange: (value: boolean) => void,
): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'settings-row';

  const labelEl = document.createElement('span');
  labelEl.className = 'settings-label settings-label-wide';
  labelEl.textContent = label;
  row.appendChild(labelEl);

  const toggle = document.createElement('button');
  toggle.className = 'settings-toggle';
  toggle.textContent = initialValue ? 'ON' : 'OFF';
  toggle.setAttribute('aria-pressed', String(initialValue));
  toggle.addEventListener('click', (e) => {
    e.stopPropagation();
    const nowOn = toggle.textContent === 'OFF';
    toggle.textContent = nowOn ? 'ON' : 'OFF';
    toggle.setAttribute('aria-pressed', String(nowOn));
    onChange(nowOn);
  });
  row.appendChild(toggle);

  return row;
}

/**
 * Segmented language picker row — "Language / 语言" label with English/中文 buttons.
 * Fires 'vn-locale-change' on document when toggled.
 */
function createLocaleRow(
  initialLocale: Locale,
  onChange: (locale: Locale) => void,
): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'settings-row';

  const labelEl = document.createElement('span');
  labelEl.className = 'settings-label settings-label-wide';
  labelEl.textContent = 'Language / 语言';
  row.appendChild(labelEl);

  const segmented = document.createElement('div');
  segmented.className = 'settings-locale-segmented';
  segmented.setAttribute('role', 'group');
  segmented.setAttribute('aria-label', 'Language');

  const makeBtn = (locale: Locale, display: string): HTMLButtonElement => {
    const btn = document.createElement('button');
    btn.className = 'settings-locale-btn';
    btn.textContent = display;
    btn.setAttribute('aria-pressed', String(locale === initialLocale));
    if (locale === initialLocale) btn.classList.add('active');

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      // Update both buttons
      enBtn.classList.toggle('active', locale === 'en');
      zhBtn.classList.toggle('active', locale === 'zh');
      enBtn.setAttribute('aria-pressed', String(locale === 'en'));
      zhBtn.setAttribute('aria-pressed', String(locale === 'zh'));
      onChange(locale);
    });

    return btn;
  };

  const enBtn = makeBtn('en', 'English');
  const zhBtn = makeBtn('zh', '中文');

  segmented.appendChild(enBtn);
  segmented.appendChild(zhBtn);
  row.appendChild(segmented);

  return row;
}
